import { useNodeByRefCallback } from 'utils/dom';

import { useCallback, useContext, useEffect, useMemo } from 'react';

import type { ITableLikeTreeItem } from '../context';
import { TableLikeTreeContext } from '../context';
import { getAllChildren, getRootItem } from '../utils';

interface IUseTableTreeDrop {
    treeItem: ITableLikeTreeItem;
    columnsCount: number;
    isDragButtonHidden: boolean | undefined;
}

const updateStyleHeight = (target: HTMLElement, newHeight: number) => {
    const placeholder = target?.closest('.placeholder') as HTMLElement;

    if (placeholder) {
        const placeholderCells = placeholder.querySelectorAll('.TableCell');

        placeholderCells.forEach((cell: any) => {
            cell.style.height = `${newHeight}px`;
        });
    }
};

const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;

    target?.classList?.add('drop-active');
    updateStyleHeight(target, 15);
};

const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;

    target?.classList?.remove('drop-active');
    updateStyleHeight(target, 0);
};

const onPointerEnter = (e: Event) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;

    if (target?.classList?.contains('drop-active')) {
        return;
    }

    target?.classList?.add('drop-active');
    updateStyleHeight(target, 15);
};

const onPointerLeave = (e: Event) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;

    if (!target?.classList?.contains('drop-active')) {
        return;
    }

    target?.classList?.remove('drop-active');
    updateStyleHeight(target, 0);
};

const onPointerForbiddenEnter = (e: Event) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement)?.classList?.add('drop-forbidden');
};

const onPointerForbiddenLeave = (e: Event) => {
    e.preventDefault();
    (e.currentTarget as HTMLElement)?.classList?.remove('drop-forbidden');
};

const createPseudoColumns = (treeItem: ITableLikeTreeItem, pseudoRow: HTMLElement, columnsCount: number, _: string) => {
    for (let i = 0; i < columnsCount; i++) {
        const pseudoColumn = document.createElement('div');
        const innerRow = document.createElement('div');

        pseudoColumn.classList.add('TableCell');
        innerRow.classList.add('innerPlaceholder');

        pseudoColumn.append(innerRow);
        pseudoRow.append(pseudoColumn);
    }
};

const createPseudoRow = (
    treeItem: ITableLikeTreeItem,
    tableRow: HTMLElement | null,
    columnsCount: number,
    key: string,
) => {
    const element = tableRow?.parentElement?.getElementsByClassName(key)?.[0];

    if (element) {
        element.remove();
    }

    const pseudoRow = document.createElement('div');

    createPseudoColumns(treeItem, pseudoRow, columnsCount, key);

    pseudoRow.classList.add('placeholder', 'Table-CellsRow', key, `treeLevel_${treeItem.treeLevel || 0}`);

    if ((treeItem.treeLevel || 0) > 2) {
        pseudoRow.classList.add('treeLevelMoreThanSecond');
    }

    return pseudoRow;
};

const createPlaceholder = (treeItem: ITableLikeTreeItem, tableRow: HTMLElement | null, columnsCount: number) => {
    const { id, isLastChild } = treeItem;

    const prependedKey = `placeholder-prepended-${id}`;
    let pseudoRow = createPseudoRow(treeItem, tableRow, columnsCount, prependedKey);

    if (pseudoRow) {
        tableRow?.parentElement?.insertBefore(pseudoRow, tableRow);
    }

    if (isLastChild) {
        const appendedKey = `placeholder-appended-${id}`;

        pseudoRow = createPseudoRow(treeItem, tableRow, columnsCount, appendedKey);

        if (pseudoRow) {
            const tableRows = tableRow?.parentElement?.children || [];
            const currentRowIndex = Array.prototype.indexOf.call(tableRows, tableRow);

            if (currentRowIndex !== -1) {
                const lastOfCurrentIndex = currentRowIndex + getAllChildren(treeItem).length;

                tableRow?.parentElement?.insertBefore(pseudoRow, tableRows[lastOfCurrentIndex + 1]);
            }
        }
    }
};

export const useTableTreeDrop = (props: IUseTableTreeDrop) => {
    const {
        currentDragged,
        onDrop,
        isItemMovableOnlyInsideRootItemBrunch,
        isItemMovableOnlyInsideParent,
        isRootItemUnableToAppend,
        isPointerDropMode,
        pointerMode,
    } = useContext(TableLikeTreeContext);
    const { treeItem, columnsCount, isDragButtonHidden = false } = props;

    const { id, parent, title } = treeItem;
    const { parent: draggedParent } = currentDragged || {};
    const [row, ref] = useNodeByRefCallback('Table-CellsRow', true);

    const isDraggedChild = useMemo(() => {
        if (!isDragButtonHidden) {
            return (
                !!currentDragged &&
                (currentDragged.id === id ||
                    getAllChildren(currentDragged)
                        .map((c) => c.id)
                        .includes(id))
            );
        }

        return false;
    }, [currentDragged, id, isDragButtonHidden]);

    const isDraggedInSameBrunch = useMemo(() => {
        if (!isDragButtonHidden) {
            const rootItem = getRootItem(treeItem);
            const rootItemBrunchIds = [rootItem.id, ...getAllChildren(rootItem).map((c) => c.id)];

            return rootItemBrunchIds.includes(currentDragged?.id as number);
        }

        return false;
    }, [currentDragged?.id, isDragButtonHidden, treeItem]);

    const onInsertHandler = useCallback(
        (insertBefore: boolean) => (e: MouseEvent | DragEvent) => {
            if (currentDragged) {
                (e.currentTarget as HTMLElement)?.classList?.remove('drop-active');
                onDrop(currentDragged, treeItem, true, insertBefore);
            }
        },
        [currentDragged, onDrop, treeItem],
    );

    const onAppendHandler = useCallback(
        (e: MouseEvent | DragEvent) => {
            if (currentDragged) {
                (e.currentTarget as HTMLElement)?.classList?.remove('drop-active');
                onDrop(currentDragged, treeItem);
            }
        },
        [currentDragged, onDrop, treeItem],
    );

    useEffect(() => {
        if (isDragButtonHidden || (!currentDragged && !isPointerDropMode) || !row) {
            return;
        }

        createPlaceholder(treeItem, row as unknown as HTMLElement, columnsCount);
    }, [columnsCount, currentDragged, isDragButtonHidden, isPointerDropMode, row, treeItem]);

    useEffect(() => {
        if (!isDragButtonHidden) {
            const currentRow = row as unknown as HTMLElement;

            const placeHolderBefore = currentRow?.parentElement?.getElementsByClassName(
                `placeholder-prepended-${id}`,
            )?.[0] as HTMLElement;
            const placeHolderAfter = currentRow?.parentElement?.getElementsByClassName(
                `placeholder-appended-${id}`,
            )?.[0] as HTMLElement;

            const handleBefore = onInsertHandler(true);
            const handleAfter = onInsertHandler(false);

            if (currentRow) {
                const draggedParentId = draggedParent?.id ?? currentDragged?.parentId;
                const isDraggedItemRoot = isItemMovableOnlyInsideParent ? draggedParentId == null : !draggedParent;

                let isInsertEnabled: boolean;
                let isAppendEnabled: boolean;

                if (isDraggedItemRoot) {
                    isInsertEnabled = !isRootItemUnableToAppend || !parent;
                    isAppendEnabled = !isRootItemUnableToAppend;
                } else if (isItemMovableOnlyInsideParent) {
                    const targetParentId = parent?.id ?? treeItem.parentId;
                    const isSameParent =
                        draggedParentId != null &&
                        targetParentId != null &&
                        String(draggedParentId) === String(targetParentId);

                    isInsertEnabled = isSameParent;
                    isAppendEnabled = draggedParentId != null && String(draggedParentId) === String(id);
                } else if (isItemMovableOnlyInsideRootItemBrunch) {
                    isInsertEnabled = isDraggedInSameBrunch && !!parent;
                    isAppendEnabled = isDraggedInSameBrunch;
                } else {
                    isInsertEnabled = true;
                    isAppendEnabled = true;
                }

                if (isDraggedChild && !isPointerDropMode) {
                    isInsertEnabled = false;
                    isAppendEnabled = false;
                }

                if (isInsertEnabled) {
                    placeHolderBefore?.addEventListener('dragover', onDragOver);
                    placeHolderBefore?.addEventListener('dragleave', onDragLeave);
                    placeHolderBefore?.addEventListener('drop', handleBefore);

                    if (isPointerDropMode) {
                        if (isDraggedChild && pointerMode !== 'copy') {
                            placeHolderBefore?.addEventListener('mouseenter', onPointerForbiddenEnter);
                            placeHolderBefore?.addEventListener('mouseleave', onPointerForbiddenLeave);
                        } else {
                            placeHolderBefore?.addEventListener('mouseenter', onPointerEnter);
                            placeHolderBefore?.addEventListener('mouseleave', onPointerLeave);
                            placeHolderBefore?.addEventListener('click', handleBefore);
                        }
                    }
                }

                if (isInsertEnabled) {
                    placeHolderAfter?.addEventListener('dragover', onDragOver);
                    placeHolderAfter?.addEventListener('dragleave', onDragLeave);
                    placeHolderAfter?.addEventListener('drop', handleAfter);

                    if (isPointerDropMode) {
                        if (isDraggedChild && pointerMode !== 'copy') {
                            placeHolderAfter?.addEventListener('mouseenter', onPointerForbiddenEnter);
                            placeHolderAfter?.addEventListener('mouseleave', onPointerForbiddenLeave);
                        } else {
                            placeHolderAfter?.addEventListener('mouseenter', onPointerEnter);
                            placeHolderAfter?.addEventListener('mouseleave', onPointerLeave);
                            placeHolderAfter?.addEventListener('click', handleAfter);
                        }
                    }
                }

                if (isAppendEnabled) {
                    currentRow.addEventListener('dragover', onDragOver);
                    currentRow.addEventListener('dragleave', onDragLeave);
                    currentRow.addEventListener('drop', onAppendHandler);

                    if (isPointerDropMode) {
                        if (isDraggedChild && pointerMode !== 'copy') {
                            currentRow.addEventListener('mouseenter', onPointerForbiddenEnter);
                            currentRow.addEventListener('mouseleave', onPointerForbiddenLeave);
                        } else {
                            currentRow.addEventListener('mouseenter', onPointerEnter);
                            currentRow.addEventListener('mouseleave', onPointerLeave);
                            currentRow.addEventListener('click', onAppendHandler);
                        }
                    }
                }

                return () => {
                    if (isInsertEnabled) {
                        placeHolderBefore?.removeEventListener('dragover', onDragOver);
                        placeHolderBefore?.removeEventListener('dragleave', onDragLeave);
                        placeHolderBefore?.removeEventListener('drop', handleBefore);
                        if (isPointerDropMode) {
                            if (isDraggedChild && pointerMode !== 'copy') {
                                placeHolderBefore?.removeEventListener('mouseenter', onPointerForbiddenEnter);
                                placeHolderBefore?.removeEventListener('mouseleave', onPointerForbiddenLeave);
                            } else {
                                placeHolderBefore?.removeEventListener('mouseenter', onPointerEnter);
                                placeHolderBefore?.removeEventListener('mouseleave', onPointerLeave);
                                placeHolderBefore?.removeEventListener('click', handleBefore);
                            }
                        }
                    }

                    if (isInsertEnabled) {
                        placeHolderAfter?.removeEventListener('dragover', onDragOver);
                        placeHolderAfter?.removeEventListener('dragleave', onDragLeave);
                        placeHolderAfter?.removeEventListener('drop', handleAfter);
                        if (isPointerDropMode) {
                            if (isDraggedChild && pointerMode !== 'copy') {
                                placeHolderAfter?.removeEventListener('mouseenter', onPointerForbiddenEnter);
                                placeHolderAfter?.removeEventListener('mouseleave', onPointerForbiddenLeave);
                            } else {
                                placeHolderAfter?.removeEventListener('mouseenter', onPointerEnter);
                                placeHolderAfter?.removeEventListener('mouseleave', onPointerLeave);
                                placeHolderAfter?.removeEventListener('click', handleAfter);
                            }
                        }
                    }

                    if (isAppendEnabled) {
                        currentRow.removeEventListener('dragover', onDragOver);
                        currentRow.removeEventListener('dragleave', onDragLeave);
                        currentRow.removeEventListener('drop', onAppendHandler);
                        if (isPointerDropMode) {
                            if (isDraggedChild && pointerMode !== 'copy') {
                                currentRow.removeEventListener('mouseenter', onPointerForbiddenEnter);
                                currentRow.removeEventListener('mouseleave', onPointerForbiddenLeave);
                            } else {
                                currentRow.removeEventListener('mouseenter', onPointerEnter);
                                currentRow.removeEventListener('mouseleave', onPointerLeave);
                                currentRow.removeEventListener('click', onAppendHandler);
                            }
                        }
                    }

                    placeHolderBefore?.remove();
                    placeHolderAfter?.remove();
                };
            }

            return () => {
                placeHolderBefore?.remove();
                placeHolderAfter?.remove();
            };
        }

        return () => undefined;
    }, [
        draggedParent,
        id,
        isDragButtonHidden,
        isDraggedChild,
        isDraggedInSameBrunch,
        isItemMovableOnlyInsideRootItemBrunch,
        isItemMovableOnlyInsideParent,
        isRootItemUnableToAppend,
        onInsertHandler,
        parent,
        row,
        title,
        isPointerDropMode,
        onAppendHandler,
        pointerMode,
        currentDragged,
        treeItem.parentId,
    ]);

    return ref;
};
